import React, { createContext, JSX, useMemo } from "react";
import { useLocation } from "react-router";
import {
  SharingPreference,
  useShowSharingPreferencesByPublicId,
} from "@rentablo/api/apiv1";
import { useMyself, UserWithType } from "@api";

export interface SharedPortfolioContextProps {
  isSharedPortfolio: boolean;
  settings: SharingPreference;
  publicId?: string;
  isAnonymized?: boolean;
  isForbidden?: boolean;
  user?: UserWithType | null;
}

export const SharedPortfolioContext =
  createContext<SharedPortfolioContextProps>({
    isSharedPortfolio: false,
    settings: {},
    isAnonymized: false,
    isForbidden: false,
    publicId: undefined,
    user: null,
  });

export const SharedPortfolioProvider = ({
  children,
}: {
  children: JSX.Element;
}) => {
  const location = useLocation();
  const sharedPortfolioId = useMemo(
    () => location.pathname.match(/\/portfolios\/([^/]+)\/?/)?.[1],
    [location.pathname]
  );
  const [isForbidden, setIsForbidden] = React.useState(false);

  const { data, isFetched } = useShowSharingPreferencesByPublicId(
    {
      publicId: sharedPortfolioId!,
    },
    {
      enabled: Boolean(sharedPortfolioId),
      onError: (err) => {
        if (err.response?.status === 404) {
          setIsForbidden(true);
        }
      },
    }
  );
  const { data: user } = useMyself({
    enabled: Boolean(sharedPortfolioId),
  });

  if (sharedPortfolioId && !isFetched) {
    return null;
  }

  return (
    <SharedPortfolioContext.Provider
      value={{
        isSharedPortfolio: Boolean(sharedPortfolioId),
        publicId: sharedPortfolioId,
        isAnonymized: data?.isAnonymized,
        isForbidden: isForbidden,
        settings: data || {},
        user,
      }}
    >
      {children}
    </SharedPortfolioContext.Provider>
  );
};

export const useSharedPortfolioContext = () => {
  const context = React.useContext(SharedPortfolioContext);

  if (!context) {
    throw new Error(
      "useSharedPortfolioContext must be used within a SharedPortfolioProvider"
    );
  }

  return context;
};
